/**
 * StateMachine - Generic state machine implementation
 *
 * Features:
 * - Explicit state transitions with guards
 * - Transition hooks (onEnter, onExit, onTransition)
 * - Transition validation
 * - History tracking
 * - Event emission for state changes
 *
 * @example
 * const machine = new StateMachine({
 *   initial: 'idle',
 *   states: {
 *     idle: {
 *       onEnter: () => console.log('Entered idle'),
 *       transitions: {
 *         start: 'running'
 *       }
 *     },
 *     running: {
 *       transitions: {
 *         stop: 'idle',
 *         pause: 'paused'
 *       }
 *     },
 *     paused: {
 *       transitions: {
 *         resume: 'running',
 *         stop: 'idle'
 *       }
 *     }
 *   }
 * });
 *
 * machine.transition('start'); // idle -> running
 * machine.can('pause'); // true
 * machine.transition('pause'); // running -> paused
 */

class StateMachine {
  /**
   * Create a StateMachine
   * @param {Object} config - State machine configuration
   * @param {string} config.initial - Initial state
   * @param {Object} config.states - State definitions
   * @param {Function} [config.onTransition] - Global transition callback
   * @param {Function} [config.logger=console] - Logger instance
   * @param {boolean} [config.enableDebug=false] - Enable debug logging
   */
  constructor(config) {
    if (!config || !config.initial || !config.states) {
      throw new Error('StateMachine requires initial state and states configuration');
    }

    this.states = config.states;
    this.currentState = config.initial;
    this.previousState = null;
    this.onTransitionCallback = config.onTransition || null;
    this.logger = config.logger || console;
    this.enableDebug = config.enableDebug || false;

    // History tracking
    this.history = [{
      state: this.currentState,
      timestamp: Date.now(),
      transition: null
    }];

    // Transition listeners
    this.listeners = [];

    // Validate initial state exists
    if (!this.states[this.currentState]) {
      throw new Error(`Initial state "${this.currentState}" not defined in states`);
    }

    // Call onEnter for initial state
    this._callHook('onEnter', this.currentState);

    if (this.enableDebug) {
      this.logger.debug(`[StateMachine] Initialized in state: ${this.currentState}`);
    }
  }

  /**
   * Get current state
   * @returns {string} Current state name
   */
  getState() {
    return this.currentState;
  }

  /**
   * Get previous state
   * @returns {string|null} Previous state name or null
   */
  getPreviousState() {
    return this.previousState;
  }

  /**
   * Check if a transition is valid from current state
   * @param {string} transitionName - Transition name
   * @returns {boolean} True if transition is valid
   */
  can(transitionName) {
    const currentStateConfig = this.states[this.currentState];
    if (!currentStateConfig || !currentStateConfig.transitions) {
      return false;
    }

    const targetState = currentStateConfig.transitions[transitionName];
    if (!targetState) {
      return false;
    }

    // Check guard condition if defined
    const guard = currentStateConfig.guards && currentStateConfig.guards[transitionName];
    if (guard && typeof guard === 'function') {
      return guard();
    }

    return true;
  }

  /**
   * Attempt a state transition
   * @param {string} transitionName - Transition name
   * @param {*} [context] - Optional context data passed to hooks
   * @returns {boolean} True if transition succeeded
   */
  transition(transitionName, context = null) {
    // Check if transition is valid
    if (!this.can(transitionName)) {
      if (this.enableDebug) {
        this.logger.debug(`[StateMachine] Invalid transition "${transitionName}" from "${this.currentState}"`);
      }
      return false;
    }

    const currentStateConfig = this.states[this.currentState];
    const targetState = currentStateConfig.transitions[transitionName];

    // Validate target state exists
    if (!this.states[targetState]) {
      throw new Error(`Target state "${targetState}" not defined in states`);
    }

    // Store previous state
    this.previousState = this.currentState;

    if (this.enableDebug) {
      this.logger.debug(`[StateMachine] Transitioning: ${this.previousState} --[${transitionName}]--> ${targetState}`);
    }

    // Call onExit hook for current state
    this._callHook('onExit', this.currentState, context);

    // Update current state
    this.currentState = targetState;

    // Call onEnter hook for new state
    this._callHook('onEnter', this.currentState, context);

    // Add to history
    this.history.push({
      state: this.currentState,
      previousState: this.previousState,
      transition: transitionName,
      timestamp: Date.now(),
      context
    });

    // Call global transition callback
    if (this.onTransitionCallback) {
      try {
        this.onTransitionCallback(this.currentState, this.previousState, transitionName, context);
      } catch (error) {
        this.logger.error('[StateMachine] Error in onTransition callback:', error);
      }
    }

    // Notify listeners
    this._notifyListeners(this.currentState, this.previousState, transitionName, context);

    return true;
  }

  /**
   * Force set state without transition validation (use with caution)
   * @param {string} stateName - Target state name
   * @param {*} [context] - Optional context data
   */
  setState(stateName, context = null) {
    if (!this.states[stateName]) {
      throw new Error(`State "${stateName}" not defined`);
    }

    if (this.enableDebug) {
      this.logger.debug(`[StateMachine] Force setting state: ${this.currentState} --> ${stateName}`);
    }

    this.previousState = this.currentState;
    this._callHook('onExit', this.currentState, context);

    this.currentState = stateName;
    this._callHook('onEnter', this.currentState, context);

    this.history.push({
      state: this.currentState,
      previousState: this.previousState,
      transition: 'FORCE_SET',
      timestamp: Date.now(),
      context
    });

    this._notifyListeners(this.currentState, this.previousState, 'FORCE_SET', context);
  }

  /**
   * Check if currently in a specific state
   * @param {string} stateName - State name to check
   * @returns {boolean} True if in specified state
   */
  is(stateName) {
    return this.currentState === stateName;
  }

  /**
   * Check if currently in any of the specified states
   * @param {...string} stateNames - State names to check
   * @returns {boolean} True if in any of the specified states
   */
  isIn(...stateNames) {
    return stateNames.includes(this.currentState);
  }

  /**
   * Get available transitions from current state
   * @returns {Array<string>} Array of valid transition names
   */
  getAvailableTransitions() {
    const currentStateConfig = this.states[this.currentState];
    if (!currentStateConfig || !currentStateConfig.transitions) {
      return [];
    }

    const transitions = Object.keys(currentStateConfig.transitions);

    // Filter by guard conditions
    return transitions.filter(transition => this.can(transition));
  }

  /**
   * Add a transition listener
   * @param {Function} listener - Callback (currentState, previousState, transition, context)
   * @returns {Function} Unsubscribe function
   */
  addListener(listener) {
    if (typeof listener !== 'function') {
      throw new TypeError('Listener must be a function');
    }

    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index !== -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get state history
   * @param {number} [limit] - Optional limit on number of history entries
   * @returns {Array} State history
   */
  getHistory(limit = null) {
    if (limit && limit > 0) {
      return this.history.slice(-limit);
    }
    return [...this.history];
  }

  /**
   * Clear history
   */
  clearHistory() {
    this.history = [{
      state: this.currentState,
      timestamp: Date.now(),
      transition: null
    }];
  }

  /**
   * Reset to initial state
   * @param {*} [context] - Optional context data
   */
  reset(context = null) {
    const initialState = this.history[0].state;
    if (this.currentState !== initialState) {
      this.setState(initialState, context);
    }
    this.clearHistory();
  }

  /**
   * Get machine statistics
   * @returns {Object} Statistics
   */
  getStats() {
    const transitionCounts = {};
    for (let i = 1; i < this.history.length; i++) {
      const entry = this.history[i];
      if (entry.transition) {
        transitionCounts[entry.transition] = (transitionCounts[entry.transition] || 0) + 1;
      }
    }

    return {
      currentState: this.currentState,
      previousState: this.previousState,
      totalTransitions: this.history.length - 1,
      transitionCounts,
      uptime: Date.now() - this.history[0].timestamp
    };
  }

  // ============== Private Methods ==============

  /**
   * Call a lifecycle hook
   * @private
   */
  _callHook(hookName, stateName, context = null) {
    const stateConfig = this.states[stateName];
    if (stateConfig && typeof stateConfig[hookName] === 'function') {
      try {
        stateConfig[hookName](context);
      } catch (error) {
        this.logger.error(`[StateMachine] Error in ${hookName} hook for state "${stateName}":`, error);
      }
    }
  }

  /**
   * Notify all listeners of state change
   * @private
   */
  _notifyListeners(currentState, previousState, transition, context) {
    for (const listener of this.listeners) {
      try {
        listener(currentState, previousState, transition, context);
      } catch (error) {
        this.logger.error('[StateMachine] Error in listener:', error);
      }
    }
  }
}

export default StateMachine;
